import React from 'react';
import { AxiosResponse } from 'axios';


export function japanTime() {
    const d = new Date();
    d.setTime(d.getTime() + (d.getTimezoneOffset() + 9 * 60) * 60 * 1000);

    return d;
}

export function timeUntil(date: Date | string | number): [number, string] {
    const diff = new Date(date).getTime() - japanTime().getTime();
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.round(((diff % 86400000) % 3600000) / 60000);

    if (hours < 0 || minutes < 0) {
        return [0, 'ended']
    }

    return days ? [days, 'days'] : (hours ? [hours, 'hours'] : [minutes, 'minutes']);
}

export function decodeHtmlEntity(str?: string) {
    if (!str) return str;

    return str.replace(/&#(\d+);/g, (match, dec) => {
        return String.fromCharCode(dec);
    });
}

export function addDays(date: Date | string | number, days: number) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);

    return d;
}

export function range(start: number, end?: number): number[] {
    if (end) {
        return [...Array(end - 1).keys()].map(i => i + start);
    }

    return [...Array(start).keys()];
}

export function objectHasValue(item: {[key:string]: any}, filter: string, keys?: string | string[]) {
    if (!keys) {
        for (const prop of Object.values(item)) {
            if (
                (typeof prop === "string" && prop.toLowerCase().includes(filter.toLowerCase())) ||
                (typeof prop === "number" && prop.toString().includes(filter))
            ) {
                return true;
            }
        }
    } else {
        if (typeof keys === "string") {
            keys = [keys];
        }

        for (const key of keys) {
            if (!(key in item)) continue;

            const prop = item[key];

            if (
                (typeof prop === "string" && prop.toLowerCase().includes(filter.toLowerCase())) ||
                (typeof prop === "number" && prop.toString().includes(filter))
            ) {
                return true;
            }
        }
    }

    return false; // Only one false here, if nothing found!
}

export class CustomSet<T> {
    private readonly is: (a: T, b: T) => boolean;
    private list: T[] = [];

    constructor(comparator: (a: T, b: T) => boolean, values?: T[]) {
        this.is = comparator;

        if (values) {
            for (const value of values) {
                this.add(value);
            }
        }
    }

    add(value: T) {
        for (let item of this.list) {
            if (this.is(item, value)) {
                return false;
            }
        }

        this.list.push(value);

        return true;
    }

    remove(value: T) {
        this.list.forEach((item, i) => {
            if (this.is(item, value)) {
                delete this.list[i];

                return true;
            }
        })

        return false;
    }

    [Symbol.iterator]() {
        return this.list.values();
    }
}

export const getByPath = (obj: Record<string, any>, path: string, defaultValue?: any): any => {
    const value: any = path.split('.').reduce((nested, key) => nested ? nested[key] : undefined, obj);

    if (!value && defaultValue !== undefined)
        return defaultValue;

    return value;
}

export const setByPath = <T,>(obj: T, path: string, value: any): T => {
    const pathKeys = path.split('.');
    const lastKey = pathKeys.pop()!;

    const root = {...obj};
    let nested: any = root;

    for (const key of pathKeys) {
        if (!nested[key] || typeof nested[key] !== 'object') {
            nested[key] = {};
        }

        if (Object.isFrozen(nested[key]))
            nested[key] = {...nested[key]}

        nested = nested[key];
    }

    nested[lastKey] = value;

    return root;
}

export function truncate(str: string, n: number){
    return (str.length > n) ? str.substr(0, n-1) + '\u2026' : str;
}

export function replaceItemAtIndex<T>(arr: T[], index: number, newValue: T) {
    if (index === -1) return arr;
    return [...arr.slice(0, index), newValue, ...arr.slice(index + 1)];
}

export function removeItemAtIndex<T>(arr: T[], index: number) {
    if (index === -1) return arr;
    return [...arr.slice(0, index), ...arr.slice(index + 1)];
}

export const nToBr = (text: string) => {
    return text.split('\n').map((item, i) => {
        return (i === 0) ? item : [<br key={i} />, item];
    });
}

export const interpolate = (template: string | undefined, model: any, defaultVal: string = '') => {
    if (!template) return defaultVal;

    return template.replace(/{([^}]*)}/g, (_match, field) => model[field] || '');
};

export const arrayToObject = <T extends {[key:string]: any}, >(arr: T[], key: string) =>
    arr.reduce((ret, a) => ({...ret,[a[key]]: a}), {} as {[p:string|number]: T});

export const arrayGrouped = <T extends {[key:string]: any}, >(arr: T[], key: string) =>
    arr.reduce((ret, a) => ({...ret, [getByPath(a, key)]: ret[getByPath(a, key)] ? [...ret[getByPath(a, key)], a] : [a]}), {} as {[p:string|number]: T[]});

export const selectOnFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => e.target.select();

export const objectToFormData = (data: any) => {
    const formData = new FormData();

    buildFormData(formData, data);

    return formData;
};

const buildFormData = (formData: FormData, data: any, parentKey?: string) => {
    if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
        // transportation call or nested
        Object
            .keys(data)
            .forEach(key => buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key)
        );
    } else {
        // exact data
        const value = data == null ? '' : data;

        formData.append(parentKey!, value);
    }
};

export const formDataToObject = (formData: FormData) => {
    const root: Record<string, any> = {};

    for (const [key, value] of formData.entries()) {
        const keys = key.split('[').map((k) => k.replace(']', ''));

        let nested = root;

        for (let i = 0; i < keys.length - 1; i++) {
            const currentKey = keys[i];
            nested[currentKey] = nested[currentKey] || (keys[i+1] === '' ? [] : {});
            nested = nested[currentKey];
        }

        const finalKey = keys[keys.length - 1];

        if (finalKey === '') {
            nested.push(value)
        } else {
            nested[finalKey] = value;
        }
    }

    return root;
}

export const createQueryString = (data: any) => {
    const arr: string[] = [];

    const pusher = (datum: any, key: string, parentKey: string = '') => {
        const val = datum[key];
        if (typeof val === 'object')
            Object.keys(val).map(k => pusher(val, k, parentKey ? `${parentKey}[${key}]` : key));
        else if (val !== undefined)
            arr.push((parentKey ? `${parentKey}[${key}]` : key) + '=' + encodeURIComponent(`${val}`.replace(/\s/g, '_')))
    }

    Object.keys(data).map(key => pusher(data, key));

    return arr.join('&')
};

export const reloadImg = async (url: string) => {
    await fetch(url, { cache: 'reload', mode: 'no-cors' });
    document.body.querySelectorAll(`img[src='${url}']`)
        .forEach(img => (img as HTMLImageElement).src = url)
};

export const swapKeyValue = (source: {[k: string]: string}) => Object.keys(source).reduce((ret, key) => {
    ret[source[key]] = key;
    return ret;
}, {} as {[k: string]: string});

export const toChunks = <T,>(array: T[], chunk_size: number): T[][] =>
    Array(Math.ceil(array.length / chunk_size))
        .fill(0)
        .map((_, i) => i * chunk_size)
        .map(begin => array.slice(begin, begin + chunk_size));


export type PromiseState = 'fulfilled' | 'rejected';
export type Resolver<T, R = any> = (value: T) => R;
export type Rejecter = (reason?: any) => void;

export class FakePromise<T> {
    private subscribers: Resolver<T>[] = [];
    private rejectedSubscribers: Rejecter[] = [];
    private state?: PromiseState;
    private result: any;

    constructor(executor: (resolve: Resolver<T>, reject: Rejecter) => void) {
        try {
            executor(this.resolve.bind(this), this.reject.bind(this));
        } catch (err: any) {
            this.reject(err);
        }
    }

    then<TResult1 = T, TResult2 = never>(
        onFulfilled?: ((value: T) => TResult1 | PromiseLike<TResult1>) | undefined | null,
        onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
    ): FakePromise<TResult1 | TResult2> {
        return new FakePromise((resolve, reject) => {
            const wrappedOnFulfilled = (value: any) => {
                try {
                    resolve(onFulfilled ? onFulfilled(value) : value);
                } catch (error) {
                    reject(error);
                }
            };

            const wrappedOnRejected = (reason: any) => {
                try {
                    reject(onRejected ? onRejected(reason) : reason);
                } catch (error) {
                    reject(error);
                }
            };

            if (this.state === 'fulfilled') {
                queueMicrotask(() => wrappedOnFulfilled(this.result));
            } else if (this.state === 'rejected') {
                queueMicrotask(() => wrappedOnRejected(this.result));
            } else {
                this.subscribers.push(wrappedOnFulfilled);
                this.rejectedSubscribers.push(wrappedOnRejected);
            }
        });
    }

    catch(onRejected: any) {
        return this.then(null, onRejected);
    }

    finally(onFinally: any) {
        return this.then(
            (value: any) => {
                onFinally();
                return value;
            },
            (reason: any) => {
                onFinally();
                throw reason;
            }
        );
    }

    resolve(value: any) {
        if (value && typeof value.then === 'function') {
            value.then(this.resolve.bind(this), this.reject.bind(this));
            return;
        }

        this.result = value;
        this.state = 'fulfilled';

        this.subscribers.forEach(subscriber => {
            queueMicrotask(() => subscriber(value));
        });
    }

    reject(reason: any) {
        this.result = reason;
        this.state = 'rejected';

        this.rejectedSubscribers.forEach(subscriber => {
            queueMicrotask(() => subscriber(reason));
        });
    }
}

export const forceDownload = (response: AxiosResponse) => {
    const href = URL.createObjectURL(response.data);

    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', response.headers['x-filename']);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
};

export function sanitizeUrl(url: string) {
    try {
        const decoded = decodeURIComponent(url).replace(/[^\w/:]/g, '')

        if (decoded.match(/^\s*(javascript|vbscript|data):/i)) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(
                    'Anchor URL contains an unsafe JavaScript/VBScript/data expression, it will not be rendered.',
                    decoded
                )
            }

            return ''
        }
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(
                'Anchor URL could not be decoded due to malformed syntax or characters, it will not be rendered.',
                url
            )
        }

        // decodeURIComponent sometimes throws a URIError
        // See `decodeURIComponent('a%AFc');`
        // http://stackoverflow.com/questions/9064536/javascript-decodeuricomponent-malformed-uri-exception
        return ''
    }

    return url;
}

export const dataURLtoFile = (dataUrl: string, filename: string) => {
    let arr = dataUrl.split(','), mime = arr[0].match(/:(.*?);/)![1],
        bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while(n--){
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {type:mime});
}

export const scrollByHash = () => {
    const hash = window.location.hash;
    if (!hash) return;
    const row = document.getElementById(hash.substring(1));
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'start', });
};

export const numberToColor = (seed: number) => {
    const random = () => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
    };

    const hexR = Math.floor(random() * 256).toString(16).padStart(2, '0');
    const hexG = Math.floor(random() * 256).toString(16).padStart(2, '0');
    const hexB = Math.floor(random() * 256).toString(16).padStart(2, '0');

    return `#${hexR}${hexG}${hexB}`;
}

export const padBetween = (start: string, longest: number, end: string, fillString?: string) => start + ''.padEnd(longest + 3 - start.length - end.length, fillString) + end;

export const modifyArray = (arr: any[], value: any, add: boolean) => {
    const temp = [...arr];
    if (add) {
        temp.push(value);
    } else {
        const index = temp.indexOf(value);
        if (index > -1) {
            temp.splice(index, 1);
        }
    }

    return temp;
}
